import React from 'react'
import { FormattedMessage } from 'react-intl'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { Button, Typography, Theme, useMediaQuery } from '@material-ui/core'
import messages from './messages'
import ResponsiveModal, { ModalSizes } from '@src/components/ResponsiveModal'
import { UseFaqs } from './faqs'

export const useStyles = makeStyles((theme: Theme) => {
  return {
    bodyText: {
      fontSize: theme.typography.pxToRem(14),
    },
    scanNowHeading: {
      paddingBottom: theme.spacing(2),
    },
    modalBody: {
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(4),
      overflow: 'auto',
      marginTop: theme.spacing(4),
      borderTop: `1px solid ${theme.palette.grey[400]}`,
      [theme.breakpoints.up('md')]: {
        overflow: 'hidden',
        marginTop: 0,
        border: 'none',
        padding: `${theme.spacing(4)}px ${theme.spacing(8)}px`,
      },
    },
    modalGrid: {
      [theme.breakpoints.up('md')]: {
        borderTop: `1px solid ${theme.palette.grey[400]}`,
        borderBottom: `1px solid ${theme.palette.grey[400]}`,
        height: 'calc(600px * .7)',
        overflowY: 'scroll',
        padding: `${theme.spacing(4)}px ${theme.spacing(3)}px 0 0`,
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          border: `1px solid ${theme.palette.grey[400]}`,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'darkgrey',
          borderRadius: '20px',
        },
      },
    },

    okButton: {
      alignSelf: 'flex-end',
      marginTop: theme.spacing(6),
    },
    answer: {
      paddingBottom: theme.spacing(4),
      fontSize: theme.typography.pxToRem(14),
    },
  }
})

interface ScanNowFAQModalProps {
  open: boolean
  setModalOpen: (open: boolean) => void
}

const ScanNowFAQModal: React.FC<ScanNowFAQModalProps> = ({
  open,
  setModalOpen,
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const faqs = UseFaqs()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')) === true

  return (
    <div>
      {open ? (
        <ResponsiveModal
          open
          size={ModalSizes.Large}
          onClose={() => setModalOpen(false)}
          aria-labelledby="scan-now-faq"
          renderTitle={() => (
            <Typography component="h2" variant="h2">
              <FormattedMessage {...messages.SCAN_NOW_FAQ} />
            </Typography>
          )}
          raisedCloseButton
        >
          <div className={classes.modalBody}>
            <div className={classes.modalGrid}>
              {Object.keys(faqs).map((faqKey) => {
                return (
                  <div key={`scan-now-faq-${faqKey}`}>
                    <Typography
                      key={`faq-question-${faqKey}`}
                      className={classes.scanNowHeading}
                      component="h3"
                      variant="h3"
                    >
                      <FormattedMessage
                        {...messages[`SCAN_NOW_FAQ_QUESTION_${faqKey}`]}
                      />
                    </Typography>
                    {faqs[faqKey].answerSubsections?.length ? (
                      faqs[faqKey].answerSubsections.map((subsection) => (
                        <Typography
                          key={`faq-answer-${faqKey}${subsection}`}
                          className={classes.answer}
                        >
                          <FormattedMessage
                            {...messages[
                              `APPLICATION_IDENTIFIERS_LINE${subsection}`
                            ]}
                            values={faqs[faqKey].messageValues}
                          />
                        </Typography>
                      ))
                    ) : (
                      <Typography
                        key={`faq-answer-${faqKey}`}
                        className={classes.answer}
                      >
                        <FormattedMessage
                          {...messages[`SCAN_NOW_FAQ_ANSWER_${faqKey}`]}
                          values={faqs[faqKey].messageValues}
                        />
                      </Typography>
                    )}
                  </div>
                )
              })}
            </div>
            <Button
              className={classes.okButton}
              variant="contained"
              color="primary"
              fullWidth={!isDesktop}
              onClick={() => setModalOpen(false)}
            >
              <FormattedMessage {...messages.OK} />
            </Button>
          </div>
        </ResponsiveModal>
      ) : null}
    </div>
  )
}

export default ScanNowFAQModal
